import React from 'react'
import {View} from 'react-native'
import Animated from 'react-native-reanimated'
import {msg, plural, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigationState} from '@react-navigation/native'

import {useMinimalShellFooterTransform} from '#/lib/hooks/useMinimalShellTransform'
import {getCurrentRoute, isTab} from '#/lib/routes/helpers'
import {makeProfileLink} from '#/lib/routes/links'
import {
  type AllNavigatorParams,
  type CommonNavigatorParams,
} from '#/lib/routes/types'
import {useGate} from '#/lib/statsig/statsig'
import {useHomeBadge} from '#/state/home-badge'
import {useUnreadMessageCount} from '#/state/queries/messages/list-conversations'
import {useUnreadNotifications} from '#/state/queries/notifications/unread'
import {useSession} from '#/state/session'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {useCloseAllActiveElements} from '#/state/util'
import {Link} from '#/view/com/util/Link'
import {Logo} from '#/view/icons/Logo'
import {Logotype} from '#/view/icons/Logotype'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import MenuAppSvg from '#/components/DAO/menu.app'
import MenuHallSvg from '#/components/DAO/menu.hall'
import MenuSquareSvg from '#/components/DAO/menu.square'
import MenuUserCenterSvg from '#/components/DAO/menu.usercenter'
import {
  Bell_Filled_Corner0_Rounded as BellFilled,
  Bell_Stroke2_Corner0_Rounded as Bell,
} from '#/components/icons/Bell'
import {
  HomeOpen_Filled_Corner0_Rounded as HomeFilled,
  HomeOpen_Stoke2_Corner0_Rounded as Home,
} from '#/components/icons/HomeOpen'
import {MagnifyingGlass_Filled_Stroke2_Corner0_Rounded as MagnifyingGlassFilled} from '#/components/icons/MagnifyingGlass'
import {MagnifyingGlass2_Stroke2_Corner0_Rounded as MagnifyingGlass} from '#/components/icons/MagnifyingGlass2'
import {
  Message_Stroke2_Corner0_Rounded as Message,
  Message_Stroke2_Corner0_Rounded_Filled as MessageFilled,
} from '#/components/icons/Message'
import {
  UserCircle_Filled_Corner0_Rounded as UserCircleFilled,
  UserCircle_Stroke2_Corner0_Rounded as UserCircle,
} from '#/components/icons/UserCircle'
import {Text} from '#/components/Typography'
import {styles} from './BottomBarStyles'

type RouteType = keyof AllNavigatorParams

const hideTabBarRoute: RouteType[] = [
  'PointsRecord',
  'HallDocList',
  'HallScoreDistributeRecordList',
  'HallNodeList',
  'MedalsWall',
  'HallAnnouncement',
  'HallAnnouncementList',
]

export function BottomBarWeb() {
  const {_} = useLingui()
  const {hasSession, currentAccount} = useSession()
  const t = useTheme()
  const footerMinimalShellTransform = useMinimalShellFooterTransform()
  const {requestSwitchToAccount} = useLoggedOutViewControls()
  const closeAllActiveElements = useCloseAllActiveElements()
  const iconWidth = 24

  const unreadMessageCount = useUnreadMessageCount()
  const notificationCountStr = useUnreadNotifications()
  const hasHomeBadge = useHomeBadge()
  const gate = useGate()

  const showSignIn = React.useCallback(() => {
    closeAllActiveElements()
    requestSwitchToAccount({requestedAccount: 'none'})
  }, [requestSwitchToAccount, closeAllActiveElements])

  const showCreateAccount = React.useCallback(() => {
    closeAllActiveElements()
    requestSwitchToAccount({requestedAccount: 'new'})
    // setShowLoggedOut(true)
  }, [requestSwitchToAccount, closeAllActiveElements])

  const currentRoute = useNavigationState(state => {
    return getCurrentRoute(state)
  })

  const hideTabBar = hideTabBarRoute.includes(currentRoute.name as RouteType)

  return (
    <Animated.View
      role="navigation"
      style={[
        styles.bottomBar,
        styles.bottomBarWeb,
        t.atoms.bg,
        t.atoms.border_contrast_low,
        footerMinimalShellTransform,
        hideTabBar && {display: 'none'},
      ]}>
      {hasSession ? (
        <>
          <NavItem
            routeName="Home"
            href="/"
            hasNew={hasHomeBadge && gate('remove_show_latest_button')}>
            {({isActive}) => {
              return (
                <>
                  <View style={a.p_xs}>
                    <MenuSquareSvg
                      active={isActive}
                      size={iconWidth}
                      style={[styles.ctrlIcon, styles.searchIcon]}
                    />
                  </View>
                  <Text
                    style={[
                      a.text_center,
                      a.text_xs,
                      {lineHeight: 12, color: isActive ? '#1083FE' : '#42576C'},
                    ]}>
                    广场
                  </Text>
                </>
              )
            }}
          </NavItem>
          <NavItem routeName="Hall" href="/hall">
            {({isActive}) => {
              return (
                <>
                  <View style={a.p_xs}>
                    <MenuHallSvg
                      active={isActive}
                      size={iconWidth}
                      style={[styles.ctrlIcon, styles.searchIcon]}
                    />
                  </View>
                  <Text
                    style={[
                      a.text_center,
                      a.text_xs,
                      {lineHeight: 12, color: isActive ? '#1083FE' : '#42576C'},
                    ]}>
                    乡建厅
                  </Text>
                </>
              )
            }}
          </NavItem>

          <NavItem routeName="Applications" href="/applications">
            {({isActive}) => {
              return (
                <>
                  <View style={a.p_xs}>
                    <MenuAppSvg
                      active={isActive}
                      size={iconWidth}
                      style={[styles.ctrlIcon, styles.searchIcon]}
                    />
                  </View>
                  <Text
                    style={[
                      a.text_center,
                      a.text_xs,
                      {lineHeight: 12, color: isActive ? '#1083FE' : '#42576C'},
                    ]}>
                    应用
                  </Text>
                </>
              )
            }}
          </NavItem>

          {hasSession && (
            <>
              {/* <NavItem
                routeName="Messages"
                href="/messages"
                notificationCount={unreadMessageCount.numUnread}
                hasNew={unreadMessageCount.hasNew}>
                {({ isActive }) => {
                  const Icon = isActive ? MessageFilled : Message
                  return (
                    <>
                      <Icon
                        aria-hidden={true}
                        width={iconWidth - 1}
                        style={[
                          styles.ctrlIcon,
                          t.atoms.text,
                          styles.messagesIcon,
                        ]}
                      />
                      <Text style={[a.text_center]}>Name</Text>
                    </>
                  )
                }}
              </NavItem> */}
              {/* <NavItem
                routeName="Notifications"
                href="/notifications"
                notificationCount={notificationCountStr}
              >
                {({ isActive }) => {
                  const Icon = isActive ? BellFilled : Bell
                  return (
                    <Icon
                      aria-hidden={true}
                      width={iconWidth}
                      style={[styles.ctrlIcon, t.atoms.text, styles.bellIcon]}
                    />
                  )
                }}
              </NavItem> */}
              <NavItem
                routeName="Profile"
                href={
                  currentAccount
                    ? makeProfileLink({
                        did: currentAccount.did,
                        handle: currentAccount.handle,
                      })
                    : '/'
                }>
                {({isActive}) => {
                  return (
                    <>
                      <View style={a.p_xs}>
                        <MenuUserCenterSvg
                          active={isActive}
                          size={iconWidth}
                          style={[styles.ctrlIcon, styles.searchIcon]}
                        />
                      </View>
                      <Text
                        style={[
                          a.text_center,
                          a.text_xs,
                          {
                            lineHeight: 12,
                            color: isActive ? '#1083FE' : '#42576C',
                          },
                        ]}>
                        个人中心
                      </Text>
                    </>
                  )
                }}
              </NavItem>
            </>
          )}
        </>
      ) : (
        <>
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingTop: 14,
              paddingBottom: 14,
              paddingLeft: 14,
              paddingRight: 6,
              gap: 8,
            }}>
            <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
              <Logo width={32} />
              {/*<View style={{paddingTop: 4}}>*/}
              {/*  <Logotype width={80} fill={t.atoms.text.color} />*/}
              {/*</View>*/}
            </View>

            <View style={[a.flex_row, a.flex_wrap, a.gap_sm]}>
              <Button
                onPress={showCreateAccount}
                label={_(msg`Create account`)}
                size="small"
                variant="solid"
                color="primary">
                <ButtonText>
                  <Trans>Create account</Trans>
                </ButtonText>
              </Button>
              <Button
                onPress={showSignIn}
                label={_(msg`Sign in`)}
                size="small"
                variant="solid"
                color="secondary">
                <ButtonText>
                  <Trans>Sign in</Trans>
                </ButtonText>
              </Button>
            </View>
          </View>
        </>
      )}
    </Animated.View>
  )
}

const NavItem: React.FC<{
  children: (props: {isActive: boolean}) => React.ReactChild
  href: string
  routeName: string
  hasNew?: boolean
  notificationCount?: string
}> = ({children, href, routeName, hasNew, notificationCount}) => {
  const {_} = useLingui()
  const {currentAccount} = useSession()
  const currentRoute = useNavigationState(state => {
    if (!state) {
      return {name: 'Home'}
    }
    return getCurrentRoute(state)
  })

  // Checks whether we're on someone else's profile
  const isOnDifferentProfile =
    currentRoute.name === 'Profile' &&
    routeName === 'Profile' &&
    (currentRoute.params as CommonNavigatorParams['Profile']).name !==
      currentAccount?.handle

  const isActive =
    currentRoute.name === 'Profile'
      ? isTab(currentRoute.name, routeName) &&
        (currentRoute.params as CommonNavigatorParams['Profile']).name ===
          (routeName === 'Profile'
            ? currentAccount?.handle
            : (currentRoute.params as CommonNavigatorParams['Profile']).name)
      : isTab(currentRoute.name, routeName)

  return (
    <Link
      href={href}
      style={[styles.ctrl]}
      navigationAction={isOnDifferentProfile ? 'push' : 'navigate'}
      aria-role="link"
      aria-label={routeName}
      accessible={true}>
      {children({isActive})}
      {notificationCount ? (
        <View
          style={[styles.notificationCount, styles.notificationCountWeb]}
          aria-label={_(
            msg`${plural(notificationCount, {
              one: '# unread item',
              other: '# unread items',
            })}`,
          )}>
          <Text style={styles.notificationCountLabel}>{notificationCount}</Text>
        </View>
      ) : hasNew ? (
        <View style={styles.hasNewBadge} />
      ) : null}
    </Link>
  )
}
